import type { VoiceSessionResponse } from '@voxa/shared';
import { PAUSE_MS, PhraseSplitter, decodePcm16 } from './phrases.ts';
import { TurnTrimmer } from './trim.ts';
import { firstLoudOffset } from './lead.ts';

/**
 * The second voice (task 6): the translation provider still translates, and
 * this speaks its translated *text* again in the ElevenLabs voice the room's
 * creator picked for this seat.
 *
 * Two stages, so it is heard later than the built-in voice: a phrase has to be
 * finished before it can be spoken (see `phrases.ts`), and then the provider
 * needs a few hundred milliseconds to start. What it buys is the voice.
 *
 * The connection is the browser's, like the translation's WebRTC: a single-use
 * token from our server, then a WebSocket straight to the provider, so speech
 * does not pass through our server. It is opened on the first phrase rather
 * than up front — a translator asleep after a minute of silence should hold
 * nothing open — and opened again, with a fresh token, if the provider closes
 * it between phrases.
 *
 * If it cannot be made to work the caller is told once, and goes back to the
 * built-in voice. A second voice that fails must not leave a call silent.
 */

/** The provider closes an idle socket; this keeps one open between phrases. */
const KEEP_ALIVE_MS = 10_000;
/** How far ahead of now the next chunk is scheduled, so playback never starts late. */
const LEAD_S = 0.05;
/**
 * Between two phrases that play back to back. The provider's own margins are
 * cut off (see `trim.ts`), so without this the last word of one phrase would
 * run into the first of the next. Short, because it is paid on every phrase.
 */
const PHRASE_GAP_S = 0.12;

export interface VoiceMetrics {
  /**
   * From a phrase being sent to its first audio arriving — the part of the
   * delay this voice adds on top of waiting for the phrase to end. The last
   * one measured; null until there is one.
   */
  voiceDelayMs: number | null;
  phrasesSent: number;
  charsSent: number;
  /** Silence cut from the ends of phrases so far — what used to queue up. */
  trimmedMs: number;
  /**
   * The longest the player has had waiting ahead of it, in ms. Near zero means
   * each phrase played as it came; growing means phrases were queuing behind
   * one another, which is what `trimmedMs` is meant to prevent.
   */
  queueMaxMs: number;
}

export interface ElevenLabsVoiceOptions {
  /** Asks our server for a fresh single-use connection. */
  fetchSession: () => Promise<VoiceSessionResponse>;
  /** Called at most once. The caller goes back to the built-in voice. */
  onFailed: (reason: string) => void;
  onMetrics?: (m: VoiceMetrics) => void;
  /**
   * The first audible moment of a phrase, on the `performance.now()` clock,
   * with when that phrase's text began arriving. For timing this voice
   * against the built-in one (see `lead.ts`).
   */
  onSpeech?: (textAt: number, at: number) => void;
}

interface ProviderMessage {
  audio?: string;
  is_final_audio_for_turn?: boolean;
  is_final?: boolean;
  error?: string;
  message?: string;
}

export class ElevenLabsVoice {
  readonly stream: MediaStream;

  readonly #opts: ElevenLabsVoiceOptions;
  readonly #ctx: AudioContext;
  readonly #out: MediaStreamAudioDestinationNode;
  readonly #splitter = new PhraseSplitter();

  #socket: WebSocket | null = null;
  #voiceId = '';
  #sampleRate = 24_000;
  /** Phrases waiting for a socket to be open, with when their text began arriving. */
  #queue: { text: string; textAt: number }[] = [];
  /** When the text still held by the splitter began arriving. */
  #textSince: number | null = null;
  #connecting = false;
  #pauseTimer: ReturnType<typeof setTimeout> | null = null;
  #keepAlive: ReturnType<typeof setInterval> | null = null;
  #carry: Uint8Array | null = null;
  #nextAt = 0;
  #trimmer: TurnTrimmer | null = null;
  /** Whether the current phrase has had anything scheduled yet. */
  #phraseStarted = false;
  /**
   * When each phrase in flight was sent, oldest first, and whether its first
   * audio has arrived. The provider answers every flush as one turn and says
   * when a turn's audio is complete, which is what keeps the delay of the
   * second phrase from being measured against the tail of the first.
   */
  #inFlight: { sentAt: number; textAt: number; heard: boolean; spoke: boolean }[] = [];
  #metrics: VoiceMetrics = {
    voiceDelayMs: null,
    phrasesSent: 0,
    charsSent: 0,
    trimmedMs: 0,
    queueMaxMs: 0,
  };
  #failed = false;
  #closed = false;

  constructor(opts: ElevenLabsVoiceOptions) {
    this.#opts = opts;
    this.#ctx = new AudioContext();
    this.#out = this.#ctx.createMediaStreamDestination();
    this.stream = this.#out.stream;
    // Created after the click that started the call, but a browser may still
    // hold it suspended; the <audio> element that plays `stream` is the one
    // that deals with autoplay refusals.
    void this.#ctx.resume().catch(() => undefined);
  }

  get metrics(): VoiceMetrics {
    return { ...this.#metrics };
  }

  /** A fragment of translated text, as the translation provider streamed it. */
  feed(text: string): void {
    if (this.#closed || this.#failed) return;
    const now = performance.now();
    const since = this.#textSince ?? now;
    const phrases = this.#splitter.push(text);
    for (const phrase of phrases) this.#say(phrase, since);
    // What is left over began in this fragment if a phrase was just cut off
    // in front of it; otherwise it is still the text that began at `since`.
    this.#textSince =
      this.#splitter.pending.trim() === '' ? null : phrases.length > 0 ? now : since;

    if (this.#pauseTimer) clearTimeout(this.#pauseTimer);
    this.#pauseTimer = setTimeout(() => {
      this.#pauseTimer = null;
      const textAt = this.#textSince ?? performance.now();
      this.#textSince = null;
      const rest = this.#splitter.drain();
      if (rest) this.#say(rest, textAt);
    }, PAUSE_MS);
  }

  /** Idempotent. Releases the socket, the timers and the audio graph. */
  close(): void {
    if (this.#closed) return;
    this.#closed = true;
    this.#stopTimers();
    this.#dropSocket(true);
    for (const track of this.stream.getTracks()) track.stop();
    void this.#ctx.close().catch(() => undefined);
  }

  #say(phrase: string, textAt: number): void {
    this.#queue.push({ text: phrase, textAt });
    this.#pump();
  }

  #pump(): void {
    if (this.#closed || this.#failed) return;
    const ws = this.#socket;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      if (!this.#connecting) void this.#connect();
      return;
    }
    while (this.#queue.length > 0) {
      const { text, textAt } = this.#queue.shift()!;
      // The trailing space keeps two phrases from being read as one word.
      ws.send(JSON.stringify({ inputs: [{ text: `${text} `, voice_id: this.#voiceId }] }));
      ws.send(JSON.stringify({ flush: true }));
      this.#inFlight.push({ sentAt: performance.now(), textAt, heard: false, spoke: false });
      this.#metrics.phrasesSent += 1;
      this.#metrics.charsSent += text.length;
    }
  }

  async #connect(): Promise<void> {
    this.#connecting = true;
    let session: VoiceSessionResponse;
    try {
      session = await this.#opts.fetchSession();
    } catch {
      this.#connecting = false;
      this.#fail('session');
      return;
    }
    if (this.#closed) return;
    this.#voiceId = session.voiceId;
    this.#sampleRate = session.sampleRate;

    const ws = new WebSocket(session.url);
    this.#socket = ws;
    let opened = false;

    ws.addEventListener('open', () => {
      opened = true;
      this.#connecting = false;
      // The dialogue socket wants its voices named before any text.
      ws.send(JSON.stringify({ voices: [this.#voiceId] }));
      this.#keepAlive = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ keep_alive: true }));
      }, KEEP_ALIVE_MS);
      this.#pump();
    });

    ws.addEventListener('message', (event) => {
      if (typeof event.data !== 'string') return;
      let m: ProviderMessage;
      try {
        m = JSON.parse(event.data) as ProviderMessage;
      } catch {
        return;
      }
      if (m.audio) this.#play(m.audio);
      if (m.is_final_audio_for_turn) {
        // What the trimmer held back was the phrase's trailing silence.
        if (this.#trimmer) this.#schedule(this.#trimmer.end());
        this.#phraseStarted = false;
        this.#inFlight.shift();
      }
      // An error arrives as a message and is followed by a close. It is the
      // only place the reason is written down, so it is taken from here.
      if (m.error) this.#fail(m.error);
    });

    ws.addEventListener('close', () => {
      if (this.#socket === ws) this.#dropSocket(false);
      this.#connecting = false;
      // A socket that never opened is a failure; one that closed after
      // working is the provider tidying up, and the next phrase reopens it.
      if (!opened) this.#fail('connect');
      else if (this.#queue.length > 0) this.#pump();
    });
  }

  #play(base64: string): void {
    if (this.#closed) return;
    const turn = this.#inFlight[0];
    if (turn && !turn.heard) {
      turn.heard = true;
      this.#metrics.voiceDelayMs = Math.round(performance.now() - turn.sentAt);
      this.#opts.onMetrics?.(this.metrics);
    }

    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    const { samples: raw, carry } = decodePcm16(bytes, this.#carry);
    this.#carry = carry;
    if (raw.length === 0) return;

    this.#trimmer ??= new TurnTrimmer(this.#sampleRate);
    const samples = this.#trimmer.push(raw);
    this.#metrics.trimmedMs = Math.round(this.#trimmer.trimmedS * 1000);
    if (samples.length === 0) return;
    const at = this.#schedule(samples);

    // When a listener first hears this phrase: its first audible sample, on
    // the playback clock, carried over to `performance.now()` so it can be
    // set against the built-in voice's onsets.
    if (turn && !turn.spoke) {
      const offset = firstLoudOffset(samples, this.#sampleRate);
      if (offset !== null) {
        turn.spoke = true;
        const heardAt = performance.now() + (at + offset - this.#ctx.currentTime) * 1000;
        this.#opts.onSpeech?.(turn.textAt, heardAt);
      }
    }
  }

  /**
   * One buffer, laid after the last one. The context resamples from the
   * provider's rate to its own. Returns when it starts, on the context clock.
   */
  #schedule(samples: Float32Array<ArrayBuffer>): number {
    if (samples.length === 0 || this.#closed) return this.#nextAt;
    const now = this.#ctx.currentTime;
    // A new phrase that would otherwise start the instant the last one ends
    // gets its breath; one that arrives after a silence needs none.
    const gap = !this.#phraseStarted && this.#nextAt > now + LEAD_S ? PHRASE_GAP_S : 0;
    this.#phraseStarted = true;

    const buffer = this.#ctx.createBuffer(1, samples.length, this.#sampleRate);
    buffer.copyToChannel(samples, 0);
    const source = this.#ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.#out);
    const at = Math.max(now + LEAD_S, this.#nextAt + gap);
    source.start(at);
    this.#nextAt = at + buffer.duration;

    const queued = Math.round((at - now) * 1000);
    if (queued > this.#metrics.queueMaxMs) this.#metrics.queueMaxMs = queued;
    return at;
  }

  #fail(reason: string): void {
    if (this.#failed || this.#closed) return;
    this.#failed = true;
    this.#stopTimers();
    this.#dropSocket(true);
    this.#queue = [];
    this.#opts.onFailed(reason);
  }

  #stopTimers(): void {
    if (this.#pauseTimer) clearTimeout(this.#pauseTimer);
    this.#pauseTimer = null;
    if (this.#keepAlive) clearInterval(this.#keepAlive);
    this.#keepAlive = null;
  }

  #dropSocket(hangUp: boolean): void {
    const ws = this.#socket;
    this.#socket = null;
    if (this.#keepAlive) clearInterval(this.#keepAlive);
    this.#keepAlive = null;
    this.#inFlight = [];
    if (!ws || !hangUp) return;
    try {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ close_socket: true }));
      ws.close();
    } catch {
      /* already gone */
    }
  }
}
